/**
 * FixturesHelper - Centralized utilities for managing the new fixtures structure
 *
 * New GameState.fixtures structure:
 * {
 *   leagues: {
 *     'League Name': {
 *       '1': Fixture[],
 *       '2': Fixture[],
 *       ...
 *     }
 *   },
 *   cups: {
 *     'Cup Name': {
 *       '23': CupFixture[],
 *       '45': CupFixture[],
 *       ...
 *     }
 *   },
 *   weeks: {
 *     '1': (Fixture | CupFixture)[],
 *     '2': (Fixture | CupFixture)[],
 *     ...
 *   }
 * }
 */

import { Fixture, CupFixture, GameState, DisplayFixture } from '../domain/entities';

export class FixturesHelper {
  /**
   * Get all league fixtures for a specific week
   */
  static getLeagueFixturesForWeek(
    fixtures: GameState['fixtures'],
    leagueName: string,
    week: number
  ): Fixture[] {
    const weekKey = week.toString();
    return fixtures.leagues?.[leagueName]?.[weekKey] || [];
  }

  /**
   * Get all league fixtures for a league (across all weeks)
   */
  static getAllLeagueFixtures(
    fixtures: GameState['fixtures'],
    leagueName: string
  ): Fixture[] {
    const leagueFixtures = fixtures.leagues?.[leagueName] || {};
    return Object.values(leagueFixtures).flat() as Fixture[];
  }

  /**
   * Get all cup fixtures for a specific week
   */
  static getCupFixturesForWeek(
    fixtures: GameState['fixtures'],
    cupName: string,
    week: number
  ): CupFixture[] {
    const weekKey = week.toString();
    return fixtures.cups?.[cupName]?.[weekKey] || [];
  }

  /**
   * Get all cup fixtures for a cup (across all weeks)
   */
  static getAllCupFixtures(
    fixtures: GameState['fixtures'],
    cupName: string
  ): CupFixture[] {
    const cupFixtures = fixtures.cups?.[cupName] || {};
    return Object.values(cupFixtures).flat() as CupFixture[];
  }

  /**
   * Get all fixtures for a specific week (league + cups)
   */
  static getFixturesForWeek(
    fixtures: GameState['fixtures'],
    week: number
  ): (Fixture | CupFixture)[] {
    const weekKey = week.toString();
    return fixtures.weeks?.[weekKey] || [];
  }

  /**
   * Add league fixtures for a week
   */
  static addLeagueFixtures(
    fixtures: GameState['fixtures'],
    leagueName: string,
    week: number | string,
    newFixtures: Fixture[]
  ): GameState['fixtures'] {
    const weekKey = week.toString();

    // Add leagueName to each fixture for lookup
    const fixturesWithLeagueName = newFixtures.map(f => ({
      ...f,
      leagueName,
    }));

    let result = {
      ...fixtures,
      leagues: {
        ...fixtures.leagues,
        [leagueName]: {
          ...fixtures.leagues?.[leagueName],
          [weekKey]: fixturesWithLeagueName,
        },
      },
    };

    // Also add to weeks for cross-competition week lookup
    result = this.addWeekFixtures(result, week, fixturesWithLeagueName);

    return result;
  }

  /**
   * Add cup fixtures for a week
   */
  static addCupFixtures(
    fixtures: GameState['fixtures'],
    cupName: string,
    week: number | string,
    newFixtures: CupFixture[]
  ): GameState['fixtures'] {
    const weekKey = week.toString();

    // Add cupName to each fixture for lookup
    const fixturesWithCupName = newFixtures.map(f => ({
      ...f,
      cupName,
    }));

    let result = {
      ...fixtures,
      cups: {
        ...fixtures.cups,
        [cupName]: {
          ...fixtures.cups?.[cupName],
          [weekKey]: fixturesWithCupName,
        },
      },
    };

    // Also add to weeks for cross-competition week lookup
    result = this.addWeekFixtures(result, week, fixturesWithCupName);

    return result;
  }

  /**
   * Update week fixtures (automatically called when league/cup fixtures are added)
   */
  static addWeekFixtures(
    fixtures: GameState['fixtures'],
    week: number,
    newFixtures: (Fixture | CupFixture)[]
  ): GameState['fixtures'] {
    const weekKey = week.toString();
    const existingWeekFixtures = fixtures.weeks?.[weekKey] || [];
    return {
      ...fixtures,
      weeks: {
        ...fixtures.weeks,
        [weekKey]: [...existingWeekFixtures, ...newFixtures],
      },
    };
  }

  /**
   * Update a league fixture result
   */
  static updateLeagueFixtureResult(
    fixtures: GameState['fixtures'],
    leagueName: string,
    week: number,
    fixtureIndex: number,
    result: any
  ): GameState['fixtures'] {
    const weekKey = week.toString();
    const leagueWeekFixtures = [...(fixtures.leagues?.[leagueName]?.[weekKey] || [])];
    if (leagueWeekFixtures[fixtureIndex]) {
      const updatedFixture = {
        ...leagueWeekFixtures[fixtureIndex],
        result,
        leagueName, // Ensure leagueName is preserved
      };
      leagueWeekFixtures[fixtureIndex] = updatedFixture;

      // Also update in weeks
      let finalFixtures = {
        ...fixtures,
        leagues: {
          ...fixtures.leagues,
          [leagueName]: {
            ...fixtures.leagues?.[leagueName],
            [weekKey]: leagueWeekFixtures,
          },
        },
      };

      // Update the same fixture in weeks
      const weekFixtures = [...(finalFixtures.weeks?.[weekKey] || [])];
      const weekFixtureIndex = weekFixtures.findIndex(
        f => f.home === updatedFixture.home && f.away === updatedFixture.away && f.week === updatedFixture.week
      );
      if (weekFixtureIndex >= 0) {
        weekFixtures[weekFixtureIndex] = updatedFixture;
        finalFixtures = {
          ...finalFixtures,
          weeks: {
            ...finalFixtures.weeks,
            [weekKey]: weekFixtures,
          },
        };
      }

      return finalFixtures;
    }
    return fixtures;
  }

  /**
   * Update a cup fixture result
   */
  static updateCupFixtureResult(
    fixtures: GameState['fixtures'],
    cupName: string,
    week: number,
    fixtureIndex: number,
    result: any
  ): GameState['fixtures'] {
    const weekKey = week.toString();
    const cupWeekFixtures = [...(fixtures.cups?.[cupName]?.[weekKey] || [])];
    if (cupWeekFixtures[fixtureIndex]) {
      const updatedFixture = {
        ...cupWeekFixtures[fixtureIndex],
        result,
        cupName, // Ensure cupName is preserved
      };
      cupWeekFixtures[fixtureIndex] = updatedFixture;

      // Also update in weeks
      let finalFixtures = {
        ...fixtures,
        cups: {
          ...fixtures.cups,
          [cupName]: {
            ...fixtures.cups?.[cupName],
            [weekKey]: cupWeekFixtures,
          },
        },
      };

      // Update the same fixture in weeks
      const weekFixtures = [...(finalFixtures.weeks?.[weekKey] || [])];
      const weekFixtureIndex = weekFixtures.findIndex(
        f => f.home === updatedFixture.home && f.away === updatedFixture.away && f.week === updatedFixture.week
      );
      if (weekFixtureIndex >= 0) {
        weekFixtures[weekFixtureIndex] = updatedFixture;
        finalFixtures = {
          ...finalFixtures,
          weeks: {
            ...finalFixtures.weeks,
            [weekKey]: weekFixtures,
          },
        };
      }

      return finalFixtures;
    }
    return fixtures;
  }

  /**
   * Get all fixtures for a team across all competitions
   * Includes placeholders for cup rounds not yet played
   * Returns fixtures sorted by week, with competition and type information
   */
  static getAllFixturesForTeam(
    fixtures: GameState['fixtures'],
    teamName: string,
    cups?: Record<string, any>
  ): (DisplayFixture | { type: 'placeholder'; week: number; reason: 'cup_week' | 'eliminated_cup'; competition: string; cupRoundName?: string })[] {
    const results: (DisplayFixture | { type: 'placeholder'; week: number; reason: 'cup_week' | 'eliminated_cup'; competition: string; cupRoundName?: string })[] = [];

    // Search league fixtures
    Object.entries(fixtures.leagues || {}).forEach(([leagueName, weeks]) => {
      Object.values(weeks).forEach((weekFixtures) => {
        (weekFixtures || []).forEach((fixture) => {
          if (fixture.home === teamName || fixture.away === teamName) {
            results.push({
              ...fixture,
              competition: leagueName,
              type: 'league',
            } as DisplayFixture);
          }
        });
      });
    });

    // Search cup fixtures
    Object.entries(fixtures.cups || {}).forEach(([cupName, weeks]) => {
      Object.values(weeks).forEach((weekFixtures) => {
        (weekFixtures || []).forEach((fixture) => {
          if (fixture.home === teamName || fixture.away === teamName) {
            results.push({
              ...fixture,
              competition: cupName,
              type: 'cup',
            } as DisplayFixture);
          }
        });
      });
    });

    // Add cup round placeholders if cups are provided
    if (cups) {
      Object.entries(cups).forEach(([cupName, cup]) => {
        if (!cup || !cup.rounds) return;

        cup.rounds.forEach((round: any) => {
          const roundWeek = Math.floor(round.week);

          // Check if team already has a fixture for this cup round
          const hasFixtureThisRound = results.some(
            f => f.competition === cupName && Math.floor(f.week) === roundWeek
          );

          if (!hasFixtureThisRound) {
            // Check if team was eliminated from this cup
            const teamFixturesInCup = this.getTeamFixturesInCompetition(fixtures, teamName, cupName, false);
            const completedFixtures = teamFixturesInCup.filter(f => f.result);
            const unplayedFixtures = teamFixturesInCup.filter(f => !f.result);

            let isEliminated = false;
            // Team is eliminated only if they have:
            // 1. Already played at least one match in the cup, AND
            // 2. Have NO unplayed fixtures scheduled, AND
            // 3. This round is after their last completed match
            if (completedFixtures.length > 0 && unplayedFixtures.length === 0) {
              const lastWeekPlayed = Math.max(...completedFixtures.map(f => Math.floor(f.week)));
              if (roundWeek > lastWeekPlayed) {
                isEliminated = true;
              }
            }

            results.push({
              type: 'placeholder',
              week: roundWeek,
              reason: isEliminated ? 'eliminated_cup' : 'cup_week',
              competition: cupName,
              cupRoundName: round.name,
            });
          }
        });
      });
    }

    // Sort by week
    return results.sort((a, b) => a.week - b.week);
  }

  /**
   * Find a fixture by team (league or cup)
   */
  static findFixtureForTeam(
    fixtures: GameState['fixtures'],
    teamName: string,
    week?: number
  ): (Fixture | CupFixture & { competition: string; type: 'league' | 'cup' })[] {
    const results: (Fixture | CupFixture & { competition: string; type: 'league' | 'cup' })[] = [];

    // Search league fixtures
    Object.entries(fixtures.leagues || {}).forEach(([leagueName, weeks]) => {
      Object.values(weeks).forEach((weekFixtures) => {
        (weekFixtures || []).forEach((fixture) => {
          if (week === undefined || fixture.week === week) {
            if (fixture.home === teamName || fixture.away === teamName) {
              results.push({ ...fixture, competition: leagueName, type: 'league' } as any);
            }
          }
        });
      });
    });

    // Search cup fixtures
    Object.entries(fixtures.cups || {}).forEach(([cupName, weeks]) => {
      Object.values(weeks).forEach((weekFixtures) => {
        (weekFixtures || []).forEach((fixture) => {
          if (week === undefined || fixture.week === week) {
            if (fixture.home === teamName || fixture.away === teamName) {
              results.push({ ...fixture, competition: cupName, type: 'cup' } as any);
            }
          }
        });
      });
    });

    return results;
  }

  /**
   * Get fixtures for team in a specific competition
   */
  static getTeamFixturesInCompetition(
    fixtures: GameState['fixtures'],
    teamName: string,
    competitionName: string,
    isLeague: boolean
  ): (Fixture | CupFixture)[] {
    const results: (Fixture | CupFixture)[] = [];

    if (isLeague) {
      const leagueWeeks = fixtures.leagues?.[competitionName] || {};
      Object.values(leagueWeeks).forEach((weekFixtures) => {
        (weekFixtures || []).forEach((fixture) => {
          if (fixture.home === teamName || fixture.away === teamName) {
            results.push(fixture);
          }
        });
      });
    } else {
      const cupWeeks = fixtures.cups?.[competitionName] || {};
      Object.values(cupWeeks).forEach((weekFixtures) => {
        (weekFixtures || []).forEach((fixture) => {
          if (fixture.home === teamName || fixture.away === teamName) {
            results.push(fixture);
          }
        });
      });
    }

    return results;
  }

  /**
   * Initialize empty fixtures structure
   */
  static initializeEmptyFixtures(): GameState['fixtures'] {
    return {
      leagues: {},
      cups: {},
      weeks: {},
    };
  }

  /**
   * Get all fixtures flattened (for backward compatibility or when all fixtures needed)
   */
  static getAllFixtures(fixtures: GameState['fixtures']): (Fixture | CupFixture)[] {
    const all: (Fixture | CupFixture)[] = [];

    // Add all league fixtures
    Object.values(fixtures.leagues || {}).forEach((weeks) => {
      Object.values(weeks).forEach((weekFixtures) => {
        all.push(...(weekFixtures || []));
      });
    });

    // Add all cup fixtures
    Object.values(fixtures.cups || {}).forEach((weeks) => {
      Object.values(weeks).forEach((weekFixtures) => {
        all.push(...(weekFixtures || []));
      });
    });

    return all;
  }

  /**
   * Initialize empty weeks for all cup rounds in fixtures.weeks
   * This reserves weeks for cups even before actual fixtures are generated
   */
  static initializeCupRoundWeeks(
    fixtures: GameState['fixtures'],
    cups: Record<string, any>
  ): GameState['fixtures'] {
    let result = { ...fixtures };

    // Ensure weeks object exists
    if (!result.weeks) {
      result.weeks = {};
    }

    // For each cup, initialize empty arrays for all round weeks
    Object.entries(cups).forEach(([cupName, cup]) => {
      if (cup && cup.rounds) {
        cup.rounds.forEach((round: any) => {
          const weekKey = round.week.toString();
          // Only initialize if the week doesn't already have this cup's fixtures
          if (!result.weeks?.[weekKey]) {
            result.weeks![weekKey] = [];
          }
        });
      }
    });

    return result;
  }

  /**
   * Get max week across all fixtures
   */
  static getMaxWeek(fixtures: GameState['fixtures']): number {
    let maxWeek = 0;

    // Check league fixtures
    Object.values(fixtures.leagues || {}).forEach((weeks) => {
      Object.keys(weeks).forEach((weekKey) => {
        const week = parseInt(weekKey, 10);
        if (week > maxWeek) maxWeek = week;
      });
    });

    // Check cup fixtures
    Object.values(fixtures.cups || {}).forEach((weeks) => {
      Object.keys(weeks).forEach((weekKey) => {
        const week = parseInt(weekKey, 10);
        if (week > maxWeek) maxWeek = week;
      });
    });

    // Check weeks
    Object.keys(fixtures.weeks || {}).forEach((weekKey) => {
      const week = parseInt(weekKey, 10);
      if (week > maxWeek) maxWeek = week;
    });

    return maxWeek;
  }
}
